/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork, put, call, select, takeEvery, take,
} from 'redux-saga/effects';

import mbpLogger from 'mbp-logger';

import * as errorActions from '../../../../../Common/ducks/Error/Error-Actions';
import * as showLoadingMessageActions from '../../../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { callHandleCartFailure } from '../../Cart-Actions';

import {
    callToTriggerValidateOrder,
    triggerValidateOrder,
    triggerValidateOrderCompleted,
} from './ValidateCart-Actions';

import * as commonSelectors from '../../../../../Common/Common-Selectors';
import takeLeading from '../../../../../../../app/helpers/sagas/takeLeading';

// TODO: Convert it to action
import { errorOperations } from '../../../../../Common/ducks/Error';
import { checkJWT } from '../../../../../Member/ducks/Auth/Auth-Operations';
import { continueToPayment, getPaymentMethod } from '../../../../../Checkout/ducks/Common/Common-Selectors';
import {
    getIsDonationOnlyItemOnCart,
    getIsPassportOnlyItemOnCart,
    getOrderId,
    getOrderItemExcludePassportAndDonation,
    getOrderItemIds,
    getOrderItemsWithError,
} from '../../Cart-Selectors';
import { clearOrderItemErrors } from '../../../../../Checkout/ducks/Common/ducks/Error/Error-Actions';
import { workerLoadExternalCartError } from '../../../ExternalCart/ExternalCart-Operations';
import { processFeatureErrors } from '../../../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import cartServiceClient from '../../../../../../../apis/cart-service-apis/cartServiceClient';
import { anchorErrorCommonMessage } from '../../../../../../../app/pages/Checkout/containers/EnterpriseCheckout/Common/Helpers/Helper';
import { processUpdateBillingAddress } from '../../../Payment/BillingAddress/BillingAddress-Operations';
import { getFeatureFlags, getOnePageCheckoutSaleForceABTest } from '../../../App/App-Selectors';
import { getOrderItemErrors } from '../../../../../Checkout/ducks/Common/ducks/Error/Error-Selector';

function* clearPaymentOrderItemErrors() {
    const itemWithError = yield select(getOrderItemsWithError);
    const clearErrors = {};
    itemWithError.forEach((item) => { clearErrors[item] = ''; });
    yield put(errorActions.loadError({
        errType: 'payment',
        errMsg: clearErrors,
    }));

    const orderId = yield select(commonSelectors.getOrderId);
    // Clear Errors recipient and gift message
    yield put(clearOrderItemErrors(
        orderId,
        'cart',
        { errType: 'clearAllFields', field: '' },
    ));
}

function validateOrderItemsForMissingDetails(recipients) {
    const errorMapping = [];
    if (recipients && recipients.length > 0) {
        recipients.forEach((orderItem) => {
            const { recipientAddress } = orderItem;
            orderItem.cartItems.forEach((item) => {
                if (!recipientAddress?.firstName || !recipientAddress?.address1) {
                    errorMapping.push({
                        fieldName: item.cartItemId,
                        defaultMessage: 'To proceed to the payment page, please enter the recipient\'s address.',
                    });
                } else if (!item.deliveryInfo?.deliveryDate) {
                    errorMapping.push({
                        fieldName: item.cartItemId,
                        defaultMessage: 'To proceed to the payment page, please select a delivery date.',
                    });
                } else if (!item.greetingMessage?.greetingMessage && (!item.greetingCards || !item.greetingCards.length === 0)) {
                    errorMapping.push({
                        fieldName: item.cartItemId,
                        defaultMessage: 'To proceed to the payment page, please enter gift message or greeting card.',
                    });
                }
            });
        });
    }

    return errorMapping;
}

function* workerValidateOrder(action) {
    const cartId = yield select(getOrderId);
    const cartItemIds = yield select(getOrderItemIds);
    const { history, billingAddressForm, pageType } = action;
    const { location: { pathname } } = history;

    try {
        const orderItemErrors = yield select(getOrderItemErrors);
        const paymentErrors = orderItemErrors?.[cartId]?.payment || {};
        const selectedPaymentMethod = yield select(getPaymentMethod);

        // This is affecting normal flow but this will broken the flow for
        // single page checkout, need to be done in a different way. CO-13042
        // if (
        //     paymentErrors?.errorMessage
        //     && paymentErrors?.validationErrors
        //     && Object.keys(paymentErrors.validationErrors).length > 0
        //     && selectedPaymentMethod?.id !== 'Klarna'
        // ) {
        //     yield put(clearOrderItemErrors(
        //         cartId,
        //         'payment',
        //         { errType: 'clearAllFields', field: '' },
        //     ));
        // }

        const jwtToken = yield call(checkJWT);
        let redirect = false;
        // Validate all order items to have recipient address, delivery date and gift message.
        const isContinueToPayment = yield select(continueToPayment);

        if (isContinueToPayment && cartId) {
            // proceed to do a server side validation - to check product availability
            const responseValidate = yield call(cartServiceClient.validateCart, { env: {}, jwtToken, cartId });

            // Billing form is present we save the billing onf the cart
            if (responseValidate?.data?.result === 'SUCCESS') {
                if (billingAddressForm) {
                    yield call(processUpdateBillingAddress, { payload: { data: billingAddressForm, history } });
                }

                redirect = true;
                // client order items error
                yield call(clearPaymentOrderItemErrors);
            }
        } else {
            const flagState = yield select(getFeatureFlags);
            const onePageCheckout = yield select(getOnePageCheckoutSaleForceABTest) || {};
            const isOnePageCheckoutEnabled = flagState['is-one-page-checkout-enabled'] === 'newflow'
                || (
                    flagState['is-one-page-checkout-enabled'] === 'salesforce'
                    && onePageCheckout?.campaign?.campaignResponses?.[0]?.userGroup === 'Default'
                );
            if (!isOnePageCheckoutEnabled) {
                // load client side error for missing address, delivery date and gift message
                const recipients = yield select(getOrderItemExcludePassportAndDonation);

                const validationError = yield call(validateOrderItemsForMissingDetails, recipients);

                if (validationError?.length > 0) {
                    yield fork(processFeatureErrors, {
                        data: {
                            orderItemId: cartId,
                            errorFeatureType: 'payment',
                            errorValues: {
                                errorKey: 'PAYMENT_ERROR',
                                errorMessage: 'To proceed to the payment page, all the required product information must be filled out.',
                                fieldErrors: validationError,
                            },
                        },
                    });
                    console.log('Error Validation info:', {
                        data: {
                            orderItemId: cartId,
                            errorFeatureType: 'payment',
                            errorValues: {
                                errorKey: 'PAYMENT_ERROR',
                                errorMessage: 'To proceed to the payment page, all the required product information must be filled out.',
                                fieldErrors: validationError,
                            },
                        },
                    });

                    throw new Error('ErrorPaymentValidation');
                }
            }
        }

        // proceed to payment page
        if (pageType !== 'single-payment-review' && (history && redirect && (pathname.indexOf('/cart') > 0 || pathname.indexOf('/shipping/') > 0))) {
            // redirect to payment page
            history.push(`/checkout/payment/${cartId}`);
        }

        yield put(triggerValidateOrderCompleted(true));
    } catch (ex) {
        if (ex.message !== 'ErrorPaymentValidation') {
            yield fork(processFeatureErrors, {
                data: {
                    orderItemId: cartId,
                    errorFeatureType: 'payment',
                    errorValues: ex.response?.data,
                },
            });
        }

        // client order items error
        yield call(clearPaymentOrderItemErrors);
        yield put(callHandleCartFailure(ex));

        if (ex?.response?.data?.detailedError?.errorKey === 'ERROR_EXTERNAL_EXPIRED_ITEMS') {
            yield fork(workerLoadExternalCartError, {
                data: {
                    itemId: null,
                    featureType: 'payment',
                    errors: ex.response?.data?.detailedError,
                },
            });
        } else if (ex.message !== 'ErrorPaymentValidation') {
            yield call(errorOperations.getError, ex, 'payment');
        }

        anchorErrorCommonMessage();

        // hide loader
        yield put(showLoadingMessageActions.flagOffShowLoadingMessage());

        const errors = yield select(commonSelectors.getError);
        mbpLogger.logError({
            function: 'workerValidateOrder',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Error validate',
            errorsObj: errors,
            jsError: ex,
        });

        // redirect to shipping page if validation fail
        const isPassportOnlyItem = yield select(getIsPassportOnlyItemOnCart);
        const isDonationOnlyItem = yield select(getIsDonationOnlyItemOnCart);

        const validationErrors = ex.response?.data?.fieldErrors || [];
        const orderItemLevelErrors = validationErrors.filter((error) => ((Number(error.fieldName) > 0)));
        const recipientErrorMessage = ex.response?.data?.errorMessage?.indexOf('Recipient\'s address') >= 0;

        if (
            history
            && !isPassportOnlyItem
            && !isDonationOnlyItem
            && (orderItemLevelErrors?.length > 0 || recipientErrorMessage)
            && (pathname.indexOf('/payment/') > 0 || pathname.indexOf('/cart/') > 0 || pathname.indexOf('/review-order/') > 0)
        ) {
            history.push(`/checkout/shipping/${cartId}`);
        }

        yield put(triggerValidateOrderCompleted(false));
    }
}

function* workerCallToTriggerValidateOrder(action) {
    yield put(showLoadingMessageActions.updateShowLoadingMessage());
    yield put(triggerValidateOrder(action.history, action.billingAddressForm));
    yield take(triggerValidateOrderCompleted().type);
    yield put(showLoadingMessageActions.flagOffShowLoadingMessage());
}

function* watchValidateOrder() {
    yield takeLeading(triggerValidateOrder().type, workerValidateOrder);
}

function* watchCallToTriggerValidateOrder() {
    yield takeEvery(callToTriggerValidateOrder().type, workerCallToTriggerValidateOrder);
}

const watchers = [
    fork(watchValidateOrder),
    fork(watchCallToTriggerValidateOrder),
];

export {
    workerValidateOrder,
    watchers,
};
